/**
 * LandDiffPanel — collapsible summary of land changes (added / removed / kept).
 *
 * Collapsed by default. Shows a one-line summary.
 * Expanded shows three fixed sections: Added, Removed, Kept.
 * The Kept section is itself sub-collapsible (collapsed by default).
 *
 * Architecture §8, AD-070, AC-231, AC-240.
 */

import { useState } from 'react';
import type { LandDiffResult } from '../../lib/land-sim/karsten-types.js';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LandDiffPanelProps {
  diff: LandDiffResult;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function DiffRow({
  name,
  quantity,
  prefix,
  borderClass,
  dimmed,
}: {
  name: string;
  quantity: number;
  prefix: '+' | '-';
  borderClass: string;
  dimmed?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-0.5 border-l-2 ${borderClass} ${dimmed ? 'opacity-40' : ''}`}
    >
      <span className={`text-[10px] font-mono ${prefix === '+' ? 'text-green-400' : 'text-red-400'}`}>
        {prefix}
      </span>
      <span className="text-[10px] text-gray-300 flex-1">{name}</span>
      {quantity > 1 && (
        <span className="text-[10px] text-gray-500 shrink-0">x{quantity}</span>
      )}
    </div>
  );
}

function SectionHeader({ label }: { label: string }) {
  return (
    <div className="px-3 py-1 text-[9px] font-semibold text-gray-500 uppercase tracking-wider bg-gray-900/60">
      {label}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Collapsible panel showing what lands are added, removed, and kept in
 * the proposal vs the current deck state.
 */
export function LandDiffPanel({ diff }: LandDiffPanelProps) {
  const [collapsed, setCollapsed] = useState(true);
  const [keptExpanded, setKeptExpanded] = useState(false);

  const addedCount = diff.added.length;
  const removedCount = diff.removed.length;
  const keptCount = diff.kept.length;

  return (
    <div className="border-b border-gray-800/60 bg-gray-900/40">
      {/* Header / toggle */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-800/30 transition-colors"
      >
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
          Land Changes
        </span>
        <span className="text-[10px] text-gray-500">
          +{addedCount} added, -{removedCount} removed, {keptCount} kept
          <span className="ml-1.5 text-gray-600">{collapsed ? '▸' : '▾'}</span>
        </span>
      </button>

      {/* Expanded content */}
      {!collapsed && (
        <div>
          {/* Added section */}
          <SectionHeader label={`Lands Added (${addedCount})`} />
          {addedCount === 0 ? (
            <div className="px-3 py-0.5 text-[10px] text-gray-600 italic">None</div>
          ) : (
            diff.added.map((entry) => (
              <DiffRow
                key={entry.name}
                name={entry.name}
                quantity={entry.quantity}
                prefix="+"
                borderClass="border-green-700"
              />
            ))
          )}

          {/* Removed section */}
          <SectionHeader label={`Lands Removed (${removedCount})`} />
          {removedCount === 0 ? (
            <div className="px-3 py-0.5 text-[10px] text-gray-600 italic">None</div>
          ) : (
            diff.removed.map((entry) => (
              <DiffRow
                key={entry.name}
                name={entry.name}
                quantity={entry.quantity}
                prefix="-"
                borderClass="border-red-800"
              />
            ))
          )}

          {/* Kept section — sub-collapsible */}
          <button
            type="button"
            onClick={() => setKeptExpanded((v) => !v)}
            className="w-full flex items-center justify-between px-3 py-1 hover:bg-gray-800/20 transition-colors"
          >
            <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">
              Lands Kept ({keptCount})
            </span>
            <span className="text-[9px] text-gray-600">
              {keptExpanded ? '▾' : '▸'}
            </span>
          </button>
          {keptExpanded && (
            keptCount === 0 ? (
              <div className="px-3 py-0.5 text-[10px] text-gray-600 italic">None</div>
            ) : (
              diff.kept.map((entry) => (
                <DiffRow
                  key={entry.name}
                  name={entry.name}
                  quantity={entry.quantity}
                  prefix="+"
                  borderClass="border-gray-700"
                  dimmed
                />
              ))
            )
          )}
        </div>
      )}
    </div>
  );
}
